import React, { CSSProperties } from 'react';
import { Helmet } from 'react-helmet-async';

import {
  Button,
  Card,
  Result,
  Skeleton,
  Table,
  Typography,
  message,
} from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useRequest } from 'ahooks';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

import {
  GetClientApprovedTemplatesQuery,
  useGetClientApprovedTemplatesQuery,
  useGetClientPracticesForPacketQuery,
} from '../graphql/generated';
import { generateLetterTemplatePDF } from '../components/templates/LetterTemplatePreview';
import { getLanguageLabel } from '../components/utils/LanguageSelect';

interface PacketSummaryRow {
  network_name: string;
  practice_name: string;
  tin: string | null | undefined;
  practice_id: string;
  requested_language: string;
  used_language: string;
  template_name: string;
  status: string;
  short_url: string;
}

const getValidFilename = (value: string) => value.replace(/[\\/:"*?<>|]/g, '-');

async function processInBatches<T>(
  items: T[],
  worker: (item: T) => Promise<void>,
  batchSize: number,
  waitTime: number
): Promise<void> {
  for (let i = 0; i < items.length; i += batchSize) {
    await Promise.all(items.slice(i, i + batchSize).map(worker));
    if (i + batchSize < items.length) {
      await new Promise((resolve) => setTimeout(resolve, waitTime));
    }
  }
}

const buildSummaryCsv = (rows: PacketSummaryRow[]) => {
  const escape = (value: string | null | undefined) =>
    `"${(value || '').replace(/"/g, '""')}"`;
  const header =
    'network_name,practice_name,tin,practice_id,requested_language,used_language,template_name,status,short_url';
  const lines = rows.map((row) =>
    [
      row.network_name,
      row.practice_name,
      row.tin,
      row.practice_id,
      row.requested_language,
      row.used_language,
      row.template_name,
      row.status,
      row.short_url,
    ]
      .map(escape)
      .join(',')
  );
  return [header, ...lines].join('\n') + '\n';
};

const pageStyle: CSSProperties = {
  maxWidth: 1120,
  margin: '0 auto',
};

const cardStyle: CSSProperties = {
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  boxShadow: '0 8px 24px rgba(15, 23, 42, 0.06)',
  marginTop: 16,
};

type ApprovedTemplateRow = GetClientApprovedTemplatesQuery['network_templates'][number];
type ApprovedLetterTemplateRow = ApprovedTemplateRow & {
  template_language: string;
  letter_template: NonNullable<ApprovedTemplateRow['letter_template']>;
};

const templateName = (record: ApprovedTemplateRow) =>
  record.letter_template?.letter_template_name ??
  record.email_template?.email_template_name ??
  record.sms_template?.sms_template_name ??
  record.template_id;

const ApprovedTemplatesTable: React.FC<{ rows: ApprovedTemplateRow[] }> = ({
  rows,
}) => (
  <Table
    dataSource={rows}
    rowKey="network_template_id"
    size="small"
    pagination={false}
    columns={[
      {
        title: 'Network',
        render: (_, record: ApprovedTemplateRow) => record.network.network_name,
      },
      { title: 'Type', dataIndex: 'template_type' },
      {
        title: 'Language',
        dataIndex: 'template_language',
        render: getLanguageLabel,
      },
      {
        title: 'Template',
        render: (_, record: ApprovedTemplateRow) => templateName(record),
      },
    ]}
  />
);

const ClientTemplatesPage: React.FC = () => {
  const approvedTemplatesQuery = useGetClientApprovedTemplatesQuery();
  const practicesQuery = useGetClientPracticesForPacketQuery();

  const approvedTemplates =
    approvedTemplatesQuery.data?.network_templates || [];
  const familyRows = (family: string | null) =>
    approvedTemplates.filter((t) => (t.template_family || null) === family);

  const downloadPacketRequest = useRequest(
    async (family: 'SVA' | 'BNL') => {
      const letterRows = familyRows(family).filter(
        (t): t is ApprovedLetterTemplateRow =>
          t.template_type === 'letter' &&
          Boolean(t.template_language) &&
          Boolean(t.letter_template)
      );
      // `${network_id}:${language}` -> approved letter template row
      const approved = new Map(
        letterRows.map((t) => [`${t.network_id}:${t.template_language}`, t])
      );

      const jszip = new JSZip();
      const folder_name = `${family} Packet Previews ${
        new Date().toISOString().split('T')[0]
      }`;
      const packetFolder = jszip.folder(folder_name);
      const summaryRows: PacketSummaryRow[] = [];

      // every network and practice in the organization is visited; practices
      // whose network has no approved template land in summary.csv as
      // no_template rather than being silently dropped
      const networks = practicesQuery.data?.networks || [];

      for (const network of networks) {
        const networkFolder = packetFolder?.folder(
          getValidFilename(network.network_name)
        );
        const practices = network.practices.filter(
          (p) =>
            !p.practice_description?.includes('#HEDR') &&
            !p.practice_description?.includes('#BNL') &&
            !p.practice_name?.includes('Unaffiliated')
        );

        await processInBatches(
          practices,
          async (practice) => {
            const requestedLanguage = practice.practice_language || 'EN';
            const exactTemplate = approved.get(
              `${network.network_id}:${requestedLanguage}`
            );
            const templateRow =
              exactTemplate || approved.get(`${network.network_id}:EN`);
            const usedLanguage = exactTemplate ? requestedLanguage : 'EN';
            const status = exactTemplate
              ? 'ok'
              : templateRow
              ? 'fallback_to_EN'
              : 'no_template';
            const summaryRow: PacketSummaryRow = {
              network_name: network.network_name,
              practice_name: practice.practice_name,
              tin: practice.tin,
              practice_id: practice.practice_id,
              requested_language: requestedLanguage,
              used_language: usedLanguage,
              template_name: templateRow ? templateName(templateRow) : '',
              status,
              short_url: '',
            };
            if (!templateRow?.letter_template) {
              summaryRows.push(summaryRow);
              return;
            }
            const template = templateRow.letter_template;
            try {
              const pdf = await generateLetterTemplatePDF(
                template.external_id,
                template.external_secondary_id,
                template.size,
                template.format,
                template.letter_template_id,
                practice.practice_id
              );
              summaryRow.short_url = pdf.mergeVars['signature_url'] || '';
              const tinSuffix = practice.tin
                ? `_${practice.tin.slice(-4)}`
                : '';
              const practiceFolder = networkFolder?.folder(
                getValidFilename(`${practice.practice_name}${tinSuffix}`)
              );
              const fileBase = getValidFilename(
                `${template.letter_template_name}_${usedLanguage}`
              );
              if (pdf.back) {
                practiceFolder?.file(`${fileBase} (Front).pdf`, pdf.front);
                practiceFolder?.file(`${fileBase} (Back).pdf`, pdf.back);
              } else {
                practiceFolder?.file(`${fileBase}.pdf`, pdf.front);
              }
            } catch (error: any) {
              summaryRow.status = `error: ${error?.message || error}`;
            }
            summaryRows.push(summaryRow);
          },
          25,
          2000
        );
      }

      packetFolder?.file('summary.csv', buildSummaryCsv(summaryRows));
      const blob = await jszip.generateAsync({ type: 'blob' });
      saveAs(blob, `${folder_name}.zip`);
      message.success(`${family} packet previews downloaded`);
    },
    { manual: true }
  );

  if (approvedTemplatesQuery.loading || practicesQuery.loading)
    return <Skeleton />;
  if (approvedTemplatesQuery.error || practicesQuery.error)
    return (
      <Result
        status="error"
        title="There was an error retrieving approved templates"
        subTitle={
          approvedTemplatesQuery.error?.message || practicesQuery.error?.message
        }
      />
    );

  const familyCard = (family: 'SVA' | 'BNL') => {
    const rows = familyRows(family);
    const hasLetterTemplates = rows.some(
      (t) => t.template_type === 'letter' && t.letter_template
    );
    return (
      <Card
        title={`${family} Packet`}
        style={cardStyle}
        extra={
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            disabled={!hasLetterTemplates}
            loading={
              downloadPacketRequest.loading &&
              downloadPacketRequest.params?.[0] === family
            }
            onClick={() => downloadPacketRequest.run(family)}
          >
            Download {family} Previews for All Practices
          </Button>
        }
      >
        {rows.length ? (
          <ApprovedTemplatesTable rows={rows} />
        ) : (
          <Typography.Text type="secondary">
            No approved {family} templates have been assigned yet. Please
            contact{' '}
            <a href="mailto:support@getmabel.com">support@getmabel.com</a>
          </Typography.Text>
        )}
      </Card>
    );
  };

  const otherRows = familyRows(null);

  return (
    <>
      <Helmet>
        <title>Approved Forms | Mabel</title>
      </Helmet>
      <div style={pageStyle}>
        <Typography.Title level={2} style={{ marginBottom: 4 }}>
          Approved Forms
        </Typography.Title>
        <Typography.Paragraph>
          These are the compliance-approved templates assigned to your networks.
          Downloads generate a preview PDF for each of your practices, using
          each practice&apos;s preferred language (falling back to English), and
          bundle them into a zip file. If you have a template that has been
          approved, but do not see it reflected in this list, contact{' '}
          <a href="mailto:support@getmabel.com">support@getmabel.com</a>
        </Typography.Paragraph>
        {familyCard('SVA')}
        {familyCard('BNL')}
        {otherRows.length ? (
          <Card title="Other Approved Templates" style={cardStyle}>
            <ApprovedTemplatesTable rows={otherRows} />
          </Card>
        ) : null}
      </div>
    </>
  );
};

export default ClientTemplatesPage;
